import { useState, useMemo } from "react";

export default function useTradebook(trades) {
  const [range, setRange] = useState("today");
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [groupBy, setGroupBy] = useState(false);

  const filteredTrades = useMemo(() => {
    const now = Date.now();

    // 🟢 Define TODAY boundaries (calendar day)
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    const todayEnd = new Date();
    todayEnd.setHours(23, 59, 59, 999);

    // 🔒 Pre-compute today's trades
    const todayTrades = trades.filter(
      t => t.ts >= todayStart.getTime() && t.ts <= todayEnd.getTime()
    );

    // 🔒 HARD RULE: "Today" means TODAY ONLY
    if (range === "today") {
      return todayTrades.filter(t => {
        if (!t.ts) return false;

        // 🔍 Search filter
        if (
          search &&
          !t.tradingsymbol.toLowerCase().includes(search.toLowerCase())
        ) {
          return false;
        }

        // 📅 Calendar (usually empty when Today is selected)
        if (startDate && t.ts < new Date(startDate).setHours(0, 0, 0, 0))
          return false;

        if (endDate && t.ts > new Date(endDate).setHours(23, 59, 59, 999))
          return false;

        return true;
      });
    }

    // 🔁 Other ranges (3d / 7d)
    return trades.filter(t => {
      if (!t.ts) return false;

      // 🔍 Search filter
      if (
        search &&
        !t.tradingsymbol.toLowerCase().includes(search.toLowerCase())
      ) {
        return false;
      }

      // 📅 Calendar filter
      if (startDate && t.ts < new Date(startDate).setHours(0, 0, 0, 0))
        return false;

      if (endDate && t.ts > new Date(endDate).setHours(23, 59, 59, 999))
        return false;

      // ⏱ Range filter
      switch (range) {
        case "3d":
          return t.ts >= now - 3 * 24 * 60 * 60 * 1000;
        case "7d":
          return t.ts >= now - 7 * 24 * 60 * 60 * 1000;
        default:
          return true;
      }
    });
  }, [trades, search, startDate, endDate, range]);

  // 📊 Grouped trades (used when "Group by Instrument" is ON)
  const groupedTrades = groupBy
    ? Object.values(
        filteredTrades.reduce((acc, t) => {
          acc[t.tradingsymbol] = acc[t.tradingsymbol] || [];
          acc[t.tradingsymbol].push(t);
          return acc;
        }, {})
      )
    : [];

  return {
    filteredTrades,
    groupedTrades,
    controls: {
      range,
      setRange,
      search,
      setSearch,
      startDate,
      setStartDate,
      endDate,
      setEndDate,
      groupBy,
      setGroupBy,
    },
  };
}
